import { openDB, type DBSchema, type IDBPDatabase, type OpenDBCallbacks } from 'idb';

/**
 * Opening this device's IndexedDB stores so that one tab can never hang
 * another.
 *
 * Reported twice from prod: after a reload, every page drew its headers and
 * no data, until every window of the app was closed. The access log showed
 * the reloaded pages asking for `/auth/me` and nothing more — startup was
 * waiting on a local store and never got it. The databases are shared by
 * every tab of the origin, and none of our connections ever let go: a tab
 * holding a connection open can stall another tab's upgrade or delete, and
 * everything queued behind that waits too — for as long as the holding tab
 * stays open. Closing all windows was the only thing that released it.
 *
 * So every store opens through here:
 *
 *  - `blocking`: another tab needs this database (an upgrade, a delete) and
 *    this connection is in the way. It is closed at once — that tab's work
 *    wins — and the app is told, so it can say this tab needs a reload.
 *  - `blocked`: this tab is the one waiting. The app is told, so it can say
 *    so instead of drawing an empty page, and report it.
 *  - `terminated`: the browser closed the connection itself.
 *
 * And a store that is slow to open at all can be raced against a timeout
 * (`openWithin`), so a page can fall back rather than wait for ever.
 */
export type StoreEvent =
  | { kind: 'blocked' | 'blocking' | 'terminated'; store: string }
  | { kind: 'slow'; store: string; afterMs: number }
  /**
   * It would not open at all. `outdated` when the stored database is a newer
   * version than this page's code asks for — another tab already runs a newer
   * build, and this one was served the old script (a service worker still
   * waiting to take over does that). The cure is the current version.
   */
  | { kind: 'failed'; store: string; outdated: boolean; error: string };

/** Report a store that would not open, and say whether this page's code is the older one. */
export function reportOpenFailure(store: string, err: unknown): void {
  const name = (err as { name?: string } | null)?.name ?? '';
  emitStoreEvent({ kind: 'failed', store, outdated: name === 'VersionError', error: String(err) });
}

const listeners = new Set<(event: StoreEvent) => void>();

/** Hear about every store that blocks, is blocked, is closed by the browser, or is slow to open. */
export function onStoreEvent(fn: (event: StoreEvent) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function emitStoreEvent(event: StoreEvent): void {
  for (const fn of listeners) {
    try { fn(event); } catch { /* a listener's failure is not the store's */ }
  }
}

export function openStore<S extends DBSchema>(
  name: string, version: number, upgrade: OpenDBCallbacks<S>['upgrade'],
): Promise<IDBPDatabase<S>> {
  return openDB<S>(name, version, {
    ...(upgrade ? { upgrade } : {}),
    blocked() { emitStoreEvent({ kind: 'blocked', store: name }); },
    blocking(_current, _wanted, event) {
      // Let go, so the tab that needs this database is not stalled by us.
      (event.target as IDBDatabase | null)?.close();
      emitStoreEvent({ kind: 'blocking', store: name });
    },
    terminated() { emitStoreEvent({ kind: 'terminated', store: name }); },
  });
}

/** A temporary store's name: the real one's, `-temp-`, when it was made, and a little noise. */
export function temporaryStoreName(prefix: string, now: number = Date.now()): string {
  return `${prefix}-temp-${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Delete temporary stores a page made and could not remove — it crashed, or
 * the browser was closed under it. Only ones over a day old: a younger one
 * may be the store another tab is working from right now, and deleting it
 * would make that tab let go of it (`blocking`). Best-effort, and only where
 * the browser can list its databases.
 */
export function sweepTemporaryStores(prefix: string, now: number = Date.now()): void {
  const list = (indexedDB as { databases?: () => Promise<Array<{ name?: string }>> }).databases;
  if (typeof list !== 'function') return;
  void list.call(indexedDB).then((found) => {
    for (const { name } of found) {
      if (!name?.startsWith(`${prefix}-temp-`)) continue;
      const made = parseInt(name.slice(prefix.length + 6).split('-')[0] ?? '', 36);
      if (Number.isFinite(made) && now - made > 86_400_000) indexedDB.deleteDatabase(name);
    }
  }).catch(() => undefined);
}

/**
 * `promise`, or `undefined` if it has not settled within `ms`.
 *
 * The promise is not cancelled — IndexedDB cannot cancel an open — so a
 * caller that gives up must deal with it resolving later (close what it
 * opened). Says so as a `slow` event, which is what tells us next time which
 * store it was.
 */
export async function openWithin<T>(promise: Promise<T>, ms: number, store: string): Promise<T | undefined> {
  // A marker of its own for "ran out of time": `undefined` is a perfectly
  // good result for a promise that has nothing to return, and reading it as a
  // timeout reported every such call as slow the moment it finished.
  const TIMED_OUT = Symbol('timed out');
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<typeof TIMED_OUT>((resolve) => { timer = setTimeout(() => resolve(TIMED_OUT), ms); });
  const winner = await Promise.race([promise, late]);
  if (timer !== undefined) clearTimeout(timer);
  if (winner === TIMED_OUT) {
    emitStoreEvent({ kind: 'slow', store, afterMs: ms });
    return undefined;
  }
  return winner;
}
